/**
 * A dropdown that stays inside the card it was opened from.
 *
 * **Why this exists rather than a `<select>`.** A native select draws its
 * option list in the browser's own layer, positioned so the chosen row sits
 * over the closed control. With seventeen options and "Offer Letter Pending"
 * selected, that puts the panel most of the way up the screen and clean
 * outside the dialog — reported as the list "going out of the form card". No
 * CSS reaches it: the panel is not in the document, so `max-height`,
 * `overflow` and a positioned ancestor all do nothing. Replacing the control
 * is the only fix.
 *
 * This renders the list as ordinary markup, so it is capped, scrolls in place,
 * and is clipped by the dialog's own scroll container — which is what keeps it
 * in the card.
 *
 * **It also searches**, which is the second half of the same complaint. Sixty
 * salary bands is a scroll nobody can aim at; typing "22" narrows it to one
 * row. The search box only appears once a list is long enough to need it —
 * three options with a filter above them looks like a form somebody forgot to
 * finish.
 *
 * **What it is not.** Not a general form control library and not a
 * multi-select: this is the one shape this application needs, which is "choose
 * one of a long list, maybe grouped, maybe with a value already stored that
 * the list does not contain". `allowCustom` covers that last case — a salary
 * of 38,500 is a real figure and the bands are a suggestion, not a rule.
 *
 * Deliberately no portal. A portalled panel would escape the dialog's clipping
 * and then need its own scroll-and-resize repositioning to stay glued to the
 * trigger, which is a lot of machinery to solve a problem that only exists
 * because of the portal. Anchored inside the scroll container, the panel moves
 * with the field for free.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

export interface SearchSelectOption {
  value: string;
  label: string;
  /** Rendered as a heading above its run of options. */
  group?: string;
}

/**
 * Whether an option should be shown for what has been typed.
 *
 * Substring rather than prefix, and case-insensitive: somebody looking for
 * "B.Sc Nursing" types "nursing", and somebody looking for ₹22,000 types
 * "22". Exported for its own test — the filter is the whole value of the
 * control and the easiest part to get quietly wrong.
 */
export function matches(option: SearchSelectOption, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  // The group is searched too, so "nursing" also finds the heading's contents
  // for anybody who types the field rather than the qualification.
  return (
    option.label.toLowerCase().includes(q)
    || (option.group ?? '').toLowerCase().includes(q)
  );
}

/** How many options before a search box earns its place. */
const SEARCHABLE_FROM = 8;

export default function SearchSelect({
  id,
  value,
  onChange,
  options,
  /** The first row, which clears the field. */
  emptyLabel = 'Not set',
  placeholder = 'Not set',
  /**
   * Keep a typed value that is not on the list.
   *
   * For the salary boxes: the bands are offered, not enforced, and rounding
   * somebody's pay to the nearest band overstates it in the record their next
   * offer is negotiated against.
   */
  allowCustom = false,
  /** Shown under a custom value, to say what will be stored. */
  customHint = 'Use what I typed',
  disabled = false,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: SearchSelectOption[];
  emptyLabel?: string;
  placeholder?: string;
  allowCustom?: boolean;
  customHint?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  /** Which row the keyboard is on. -1 is the clear-it row. */
  const [active, setActive] = useState(-1);
  /** True when the panel has to open upward to fit. */
  const [above, setAbove] = useState(false);

  const box = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);

  const searchable = options.length >= SEARCHABLE_FROM;
  const shown = useMemo(
    () => options.filter((option) => matches(option, query)),
    [options, query],
  );

  /**
   * What the closed control reads.
   *
   * Falls back to the raw value rather than the placeholder when the list has
   * no matching option: a stored figure the bands do not contain is still what
   * is on the record, and showing "Not set" over it would be a lie the next
   * save makes true.
   */
  const selected = options.find((option) => option.value === value);
  const shows = selected?.label ?? (value || null);

  const custom = allowCustom && query.trim() !== ''
    && !shown.some((option) => option.label.toLowerCase() === query.trim().toLowerCase());

  /** Close on a click anywhere else, the same way AlertBell does. */
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (box.current && !box.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  // Opening resets the search, so the second open is not filtered by the
  // first one's typing, and puts the cursor where the next keystroke goes.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(-1);
    field.current?.focus();
  }, [open]);

  /**
   * Open upward when there is not room below.
   *
   * Measured against the viewport rather than the dialog, because the panel is
   * capped at 15rem and the question is only ever "does that fit". A field low
   * in a tall dialog would otherwise open into the footer and be cut off by
   * the scroll container — the same complaint as the native panel, arrived at
   * from the other direction.
   */
  useLayoutEffect(() => {
    if (!open || !trigger.current) return;
    const rect = trigger.current.getBoundingClientRect();
    const PANEL = 260;
    setAbove(
      rect.bottom + PANEL > window.innerHeight && rect.top > PANEL,
    );
  }, [open]);

  // Keep the keyboard's row in view without scrolling the dialog behind it.
  useEffect(() => {
    if (!open || active < 0) return;
    list.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  function choose(next: string) {
    onChange(next);
    setOpen(false);
    trigger.current?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => Math.min(i + 1, shown.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, -1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (active >= 0 && shown[active]) choose(shown[active].value);
      else if (custom) choose(query.trim());
      else if (active === -1 && !custom) choose('');
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      trigger.current?.focus();
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  }

  /** Headings are drawn when the group changes, so filtering drops empty ones. */
  let lastGroup: string | undefined;

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        id={id}
        ref={trigger}
        className="input flex items-center justify-between gap-2 text-left disabled:bg-gray-50 disabled:text-gray-400"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          // Opening with the keyboard, so the control is reachable without a
          // mouse. Once open, the search box owns the keys.
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className={`truncate ${shows ? '' : 'text-gray-400'}`}>
          {shows ?? placeholder}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
      </button>

      {open && (
        <div
          className={`absolute z-40 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-200 ${
            above ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {searchable && (
            <div className="flex items-center gap-2 border-b border-gray-100 px-2.5 py-2">
              <Search className="h-3.5 w-3.5 shrink-0 text-gray-400" />
              <input
                ref={field}
                className="w-full border-0 p-0 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
                placeholder="Type to search…"
                spellCheck={false}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(-1);
                }}
                onKeyDown={onKeyDown}
              />
            </div>
          )}

          <div
            ref={list}
            role="listbox"
            className="max-h-60 overflow-y-auto py-1 text-sm"
            // Focusable so the arrow keys work on a short list, which has no
            // search box to own them.
            tabIndex={searchable ? -1 : 0}
            onKeyDown={searchable ? undefined : onKeyDown}
          >
            {/* Clearing the field is a row rather than a cross in the corner:
                "Not set" is a real answer here — nobody asked — and it reads
                as one option among the others. */}
            {query.trim() === '' && (
              <button
                type="button"
                role="option"
                aria-selected={value === ''}
                data-index={-1}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${
                  active === -1 ? 'bg-indigo-50' : ''
                } ${value === '' ? 'font-medium text-gray-900' : 'text-gray-500'}`}
                onMouseEnter={() => setActive(-1)}
                onClick={() => choose('')}
              >
                <Check
                  className={`h-3.5 w-3.5 shrink-0 ${
                    value === '' ? 'text-indigo-600' : 'invisible'
                  }`}
                />
                {emptyLabel}
              </button>
            )}

            {shown.map((option, index) => {
              const heading = option.group && option.group !== lastGroup
                ? option.group
                : null;
              lastGroup = option.group;
              return (
                <div key={option.value}>
                  {heading && (
                    <p className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                      {heading}
                    </p>
                  )}
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    data-index={index}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${
                      active === index ? 'bg-indigo-50' : ''
                    } ${
                      option.value === value
                        ? 'font-medium text-gray-900'
                        : 'text-gray-700'
                    }`}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => choose(option.value)}
                  >
                    <Check
                      className={`h-3.5 w-3.5 shrink-0 ${
                        option.value === value ? 'text-indigo-600' : 'invisible'
                      }`}
                    />
                    <span className="truncate">{option.label}</span>
                  </button>
                </div>
              );
            })}

            {/* What was typed, when it is not on the list. Offered rather than
                assumed: pressing Enter on it stores exactly those characters,
                so a real 38,500 survives a ladder that jumps to 39,000. */}
            {custom && (
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="flex w-full items-center gap-2 border-t border-gray-100 px-3 py-1.5 text-left text-gray-700"
                onClick={() => choose(query.trim())}
              >
                <Check className="invisible h-3.5 w-3.5 shrink-0" />
                <span className="truncate">
                  {query.trim()}
                  <span className="ml-1.5 text-xs text-gray-400">{customHint}</span>
                </span>
              </button>
            )}

            {shown.length === 0 && !custom && (
              <p className="px-3 py-2 text-gray-500">Nothing matches that.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
